import React, { ReactNode } from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ListTodo, User, FileText, Briefcase } from "lucide-react";
import api from "../services/api";
import { getCurrentUser } from "../utils/auth";
import "./DashboardStats.css";

interface DashboardStatsProps {
  userId?: string | number;
  candidatesCount?: number;
  applicationsCount?: number;
  jobsCount?: number;
}

interface StatItem {
  id: string;
  title: string;
  count: number;
  icon: ReactNode;
  color: string;
  link: string;
}

interface PaginatedResponse {
  data?: unknown[];
  total?: number;
  pagination?: {
    total?: number;
  };
}

const DashboardStats: React.FC<DashboardStatsProps> = ({
  userId,
  candidatesCount = 0,
  applicationsCount = 0,
  jobsCount = 0,
}) => {
  const { t } = useTranslation("common");

  // Get current user from auth utility
  const user = getCurrentUser() || { role: "", username: "" };

  // Fetch pending tasks count (only query DashboardStats owns — Dashboard doesn't fetch todos count)
  const { data: todosData } = useQuery<PaginatedResponse, Error, number>(
    ["todos-count", userId],
    () =>
      api.todo.getTodos({
        status: "pending",
        limit: 1,
      }) as Promise<PaginatedResponse>,
    {
      enabled: !!userId,
      staleTime: 30000,
      select: (data) => data?.pagination?.total || 0,
    },
  );

  // Helper function to get applications title based on role
  const getApplicationsTitle = (): string => {
    if (user.role === "sales") return t("dashboard.applicationsForMyJobs");
    if (user.role === "recruiter")
      return t("dashboard.applicationsForMyCandidates");
    return t("dashboard.myOpenApplications");
  };

  // Design System colors: Primary #1E40AF, Secondary #3B82F6, CTA #F59E0B
  const stats: StatItem[] = [
    {
      id: "pending-tasks",
      title: t("dashboard.pendingTasks"),
      count: todosData || 0,
      icon: <ListTodo size={16} />,
      color: "#F59E0B", // CTA amber - draws attention to action items
      link: "/dashboard#tasks",
    },
    {
      id: "candidates",
      title:
        user.role === "recruiter"
          ? t("dashboard.myAssignedCandidates")
          : t("dashboard.myActiveCandidates"),
      count: candidatesCount,
      icon: <User size={16} />,
      color: "#1E40AF", // Primary blue
      link: "/candidates",
    },
    {
      id: "applications",
      title: getApplicationsTitle(),
      count: applicationsCount,
      icon: <FileText size={16} />,
      color: "#10B981", // Green - represents progress
      link: "/pipeline",
    },
    {
      id: "jobs",
      title:
        user.role === "sales" || user.role === "recruiter"
          ? t("dashboard.myAssignedJobs")
          : t("dashboard.activeJobs"),
      count: jobsCount,
      icon: <Briefcase size={16} />,
      color: "#8B5CF6", // Purple - visual variety
      link: "/jobs",
    },
  ];

  return (
    <div className="dashboard-stats">
      {stats.map((stat) => (
        <Link key={stat.id} to={stat.link} className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: stat.color }}>
            {stat.icon}
          </div>
          <div className="stat-content">
            <div className="stat-count">{stat.count}</div>
            <div className="stat-title">{stat.title}</div>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default DashboardStats;
